import { HUE_TOKENS, ensureHueStyles } from '../util/hues.js';

/**
 * <g-column> - Kanban Column
 *
 * A single column in a kanban board with header and droppable card area.
 *
 * @example
 * <g-column status="todo" label="To Do" sublabel="3 tasks" color="purple">
 *   <g-card card-id="1">...</g-card>
 * </g-column>
 *
 * @example Header with action buttons
 * <g-column status="todo" label="To Do" color="purple">
 *   <button slot="header-action" class="btn btn-xs btn-ghost btn-circle">+</button>
 *   <g-card card-id="1">...</g-card>
 * </g-column>
 *
 * @attr {string} status - Status value for cards in this column
 * @attr {string} label - Display label in header
 * @attr {string} color - Accent color. A DaisyUI semantic token (primary, secondary,
 *   accent, info, success, warning, error, neutral) follows the active theme and is
 *   preferred; the legacy named colors (purple, blue, cyan, orange, green, red, pink,
 *   yellow, gray) and any raw CSS color still work.
 * @attr {boolean|number} tint - Presence = also tint the column background with `color`.
 *   Optional value is the mix strength in percent (default 10).
 * @attr {string} sublabel - Optional sublabel (e.g., "3 deals · $450K")
 * @slot header-action - Optional buttons/icons displayed in the header (right side)
 */

// DaisyUI semantic tokens resolve to theme variables, so a column accented with
// `color="primary"` re-colors itself when the theme changes. Everything else is an
// absolute value and is passed straight through.
// The theme hues (orange, pink, purple, teal — util/hues.js) are theme variables
// too, so those names now follow the theme instead of the fixed hex below.
const SEMANTIC_TOKENS = new Set([
  'primary', 'secondary', 'accent', 'info', 'success', 'warning', 'error', 'neutral',
  ...HUE_TOKENS,
]);

const NAMED_COLORS = {
  purple: '#8b5cf6',
  blue: '#3b82f6',
  cyan: '#06b6d4',
  orange: '#f97316',
  green: '#22c55e',
  red: '#ef4444',
  pink: '#ec4899',
  yellow: '#eab308',
  gray: '#6b7280',
};

const DEFAULT_TINT = 10;

// A column is a flex column that scrolls when the board has a height. Its
// cards keep their size and the column scrolls; flex's default shrink
// squeezed 40 cards into one screen as unreadable slivers instead.
function ensureColumnStyles() {
  if (typeof document === 'undefined' || document.getElementById('g-column-styles')) return;
  const style = document.createElement('style');
  style.id = 'g-column-styles';
  style.textContent = 'g-column > * { flex-shrink: 0; }';
  (document.head || document.documentElement).appendChild(style);
}

function escapeHtml(s) {
  return String(s).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
}

export class GColumn extends HTMLElement {
  static get observedAttributes() {
    return ['label', 'color', 'sublabel', 'tint'];
  }

  constructor() {
    super();
  }

  connectedCallback() {
    ensureHueStyles();
    this.render();
  }

  attributeChangedCallback(name) {
    if (!this.isConnected) return;

    if (name === 'color' || name === 'tint') {
      this.updateColor();
    }
    this.updateHeader();
  }

  // Presence of `tint` opts the column background in. An optional numeric value
  // overrides the mix strength: tint="20" is twice as saturated as bare tint.
  get tintStrength() {
    if (!this.hasAttribute('tint')) return null;
    const raw = parseFloat(this.getAttribute('tint'));
    return Number.isFinite(raw) && raw > 0 && raw <= 100 ? raw : DEFAULT_TINT;
  }

  updateColor() {
    const colorAttr = this.getAttribute('color');
    const color = colorAttr ? this.getColor(colorAttr) : null;

    this.style.borderTop = color ? `4px solid ${color}` : '';

    // Background tint derived from the SAME value as the top border, so a board
    // only ever names its colour once. Mixed INTO base-200 rather than over
    // transparent: the column keeps the surface identity every other panel has,
    // and a saturated accent becomes a wash instead of a block of colour that
    // strands the foreground text on dark themes. If color-mix() is unavailable
    // the declaration is dropped and the bg-base-200 class still applies.
    const strength = this.tintStrength;
    this.style.backgroundColor = (color && strength != null)
      ? `color-mix(in oklab, ${color} ${strength}%, var(--color-base-200))`
      : '';
  }

  getColor(color) {
    // Semantic tokens become theme variables, so the column re-colours itself
    // when the theme changes. Previously these fell through to the raw-value
    // branch and produced an invalid CSS colour, silently dropping the border.
    if (SEMANTIC_TOKENS.has(color)) return `var(--color-${color})`;
    return NAMED_COLORS[color] || color || NAMED_COLORS.gray;
  }

  render() {
    // Use Tailwind classes for styling
    // Note: width (w-[300px] or flex-1) is controlled by parent g-kanban
    // The column scrolls its own cards when the board has a height; its
    // header stays pinned (updateHeader).
    ensureColumnStyles();
    this.classList.add(
      'flex', 'flex-col', 'shrink-0', 'rounded-xl', 'p-3', 'pt-0',
      'bg-base-200', 'min-h-0', 'overflow-x-hidden', 'overflow-y-auto'
    );

    // Colored top border + optional tint (the only styles that must be inline)
    this.updateColor();

    // Create header
    this.updateHeader();
  }

  updateHeader() {
    const label = this.getAttribute('label') || '';
    const sublabel = this.getAttribute('sublabel') || '';

    let header = this.querySelector('.g-column-header');

    // Preserve existing action elements before rebuilding
    const existingActions = header?.querySelectorAll('.g-column-actions > *');
    const savedActions = existingActions ? [...existingActions] : [];

    if (!header) {
      header = document.createElement('div');
      this.insertBefore(header, this.firstChild);
    }

    header.className = 'g-column-header sticky top-0 z-[1] bg-inherit flex justify-between items-start py-3 px-1 mb-1';

    // Escaped: these attributes routinely carry record data (an Alpine
    // :label="deal.name" binding puts user content straight in here).
    header.innerHTML = `
      <div>
        <div class="font-semibold text-base-content">${escapeHtml(label)}</div>
        ${sublabel ? `<div class="text-xs text-base-content/50 mt-0.5">${escapeHtml(sublabel)}</div>` : ''}
      </div>
      <div class="g-column-actions flex gap-1"></div>
    `;

    const actionsContainer = header.querySelector('.g-column-actions');

    // Restore saved actions, or collect new ones on first render
    if (savedActions.length > 0) {
      savedActions.forEach(el => actionsContainer.appendChild(el));
    } else {
      const newActions = this.querySelectorAll('[slot="header-action"]');
      newActions.forEach(el => actionsContainer.appendChild(el));
    }
  }
}

if (!customElements.get('g-column')) {
  customElements.define('g-column', GColumn);
}
